import React, { useState } from "react";

import { LeafIcon as Leaf } from "@packages/ds";
import { useIntl } from "react-intl";

import Dialog from "ds/Dialog";
import SettingLine from "ds/SettingLine";
import { formatAsCurrency } from "Libs/utils";
import { selectOrganizationSubscriptions } from "Reducers/organization/subscription";
import * as O from "src/organization/pages/billing/components/Overview/BillingOverview.styles";
import {
  countTaxValue,
  countValue,
  lineItemsByOrder
} from "src/organization/pages/billing/util";
import { useAppSelector } from "Store/hooks";

import * as S from "./InvoiceProjects.styles";
import ProjectDetail from "./ProjectDetail";

import type { Order } from "@packages/client";

type InvoiceProjectsProps = {
  order: Order;
  orderCurrency?: string;
  organizationId?: string;
};

const InvoiceProjects = ({
  order,
  orderCurrency,
  organizationId
}: InvoiceProjectsProps) => {
  const intl = useIntl();
  const [expandedItems, setExpandedItems] = useState<string[]>([]);

  const subscriptions = useAppSelector(state =>
    selectOrganizationSubscriptions(state, {
      organizationId: organizationId ?? ""
    })
  );

  const onArrowClick = (subscriptionId: string) => {
    setExpandedItems(items =>
      items.includes(subscriptionId)
        ? items.filter(item => item !== subscriptionId)
        : [...items, subscriptionId]
    );
  };

  const lineItems = lineItemsByOrder(order);

  if (!lineItems) {
    return null;
  }

  const listItemsIncludedFromInvoice = Object.values(lineItems).filter(
    item => item.title
  ).length;

  return (
    <S.InvoiceOrganizationWrapper>
      <S.Title>
        {intl.formatMessage({
          id: `billing.invoice.projects`
        })}
        <S.ProjectsNumber>({listItemsIncludedFromInvoice})</S.ProjectsNumber>
      </S.Title>
      {Object.keys(lineItems).map(subscriptionId => {
        if (
          lineItems[subscriptionId].line_items.every(
            lineItem => lineItem.exclude_from_invoice
          )
        ) {
          return null;
        }

        const discounts = lineItems[subscriptionId].line_items
          .filter(item => !item.exclude_from_invoice)
          .map(li => li.components)
          .flatMap(c => Object.keys(c))
          .filter((value, index, keys) => keys.indexOf(value) === index)
          .filter(value => value.match(/discount/i))
          .map(key => ({
            key,
            value: countValue({
              lineItems,
              key,
              subscriptionId
            })
          }));

        const projectSubtotal = countValue({
          key: "base_price",
          lineItems,
          subscriptionId
        });

        const taxes = countTaxValue({
          subscriptionId,
          lineItems
        });

        const discountsTotal = discounts.reduce((accumulator, currentValue) => {
          return accumulator + currentValue.value;
        }, 0);

        const taxTotal = taxes?.reduce((acc, curr) => acc + curr.value, 0) ?? 0;

        const total =
          Math.round((projectSubtotal + discountsTotal + taxTotal) * 100) / 100;

        const subscription = subscriptions.find(s => s?.id === subscriptionId);

        const isRegionGreen = subscription?.green;

        return (
          <React.Fragment key={subscriptionId}>
            <SettingLine
              disableBoxShadow={true}
              variant="short"
              id={`billing-history-recurring-${subscriptionId}`}
              openText=""
              customAction={undefined}
              info={
                <S.ProjectName>
                  <S.TitleIconWrapper>
                    {lineItems[subscriptionId].title
                      ? lineItems[subscriptionId].title
                      : "Untitled Project"}
                    {!!isRegionGreen && (
                      <Dialog
                        trigger={
                          <O.IconWrapper aria-label="leaf">
                            <Leaf color="var(--theme-success-600)" />
                          </O.IconWrapper>
                        }
                        analyticId="green_discount.description"
                        title={intl.formatMessage({
                          id: "green_discount.title"
                        })}
                        description={intl.formatMessage({
                          id: "green_discount.description"
                        })}
                      />
                    )}
                  </S.TitleIconWrapper>
                  <div>{formatAsCurrency(total, orderCurrency)}</div>
                </S.ProjectName>
              }
              isOpen={expandedItems.includes(subscriptionId)}
              onClick={() => {
                onArrowClick(subscriptionId);
              }}
            >
              {expandedItems.includes(subscriptionId) &&
                lineItems[subscriptionId] && (
                  <ProjectDetail
                    lineItemsByOrder={lineItems}
                    lineItemsProjectSubtotal={projectSubtotal}
                    lineItemsProjectTotal={total}
                    subscriptionId={subscriptionId}
                    taxes={taxes}
                    discounts={discounts}
                  />
                )}
            </SettingLine>
          </React.Fragment>
        );
      })}
    </S.InvoiceOrganizationWrapper>
  );
};

export default InvoiceProjects;
